import {useEffect, createContext, useContext} from 'react'
import {twJoin} from 'tailwind-merge'

import {addClassToBody, injectStyle, IS_TEST} from '@sdox/utils/dom'

import type {XcThemeFragment} from '@sdox/api'
import type {ReactNode} from 'react'

const getThemeClassNames = (xcTheme: XcThemeFragment | null | undefined) => {
  if (!xcTheme) return ''

  const {id, cssVariables} = xcTheme
  let radiusClassName = ''
  try {
    const preset = cssVariables['--radius-preset']
    radiusClassName = preset ? `radius-${preset}` : ''
  } catch (e) {
    // no error
  }

  return `theme-${id} structure-tokens ${radiusClassName}`
}

export interface DocumentThemeProps {
  children?: ReactNode
  xcTheme?: XcThemeFragment | null
  className?: string
  includeThemeOnBody?: boolean
}

export type XcThemeContextState = {
  xcTheme?: XcThemeFragment
}

export const XcThemeContext = createContext<XcThemeContextState>({
  xcTheme: undefined,
})

export const useXcTheme = () => useContext(XcThemeContext)

export const ThemeProvider = ({
  children,
  className,
  xcTheme,
  includeThemeOnBody = false,
}: DocumentThemeProps) => {
  const {id, cssHeader, cssVariablesStr} = xcTheme ?? {}
  const themeId = `theme-${id}`
  const styleContent = [
    cssHeader,
    `.${themeId}`,
    `{${cssVariablesStr}};`,
  ].filter(Boolean).join(' ')

  useEffect(() => {
    if (styleContent) {
      injectStyle(themeId, styleContent)
    }
    if (includeThemeOnBody) {
      addClassToBody(getThemeClassNames(xcTheme))
    }
  }, [xcTheme, styleContent, themeId, includeThemeOnBody])

  return (
    <XcThemeContext.Provider value={{xcTheme: xcTheme ?? undefined}}>
      <div
        data-theme={id}
        className={twJoin(
          getThemeClassNames(xcTheme),
          'font-body', // one line to rule them all
          className,
        )}
      >
        {IS_TEST ? <div data-testid={themeId}>{styleContent}</div> : null}
        {children}
      </div>
    </XcThemeContext.Provider>
  )
}
